import { useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarRange, Pencil, Plus, Trash2 } from "lucide-react";

import { api } from "../../services/api";
import { timetableService, type TimetablePeriod } from "../../services/timetableService";
import { apiErrorMessage } from "../../utils/apiError";
import { ConfirmDialog, type ConfirmRequest } from "../ConfirmDialog";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, UsageNotice, deleteErrorText, type NoticeState } from "../register/RecordParts";
import { describeUsage } from "../register/registerLogic";
import {
  PERIOD_KINDS,
  assignmentConfig,
  clock,
  isNurseryLevel,
  kindLabel,
  periodConfig,
  periodGaps,
  periodMinutes,
  periodTime,
  ugandaSchoolDay,
  type Assignment,
  type DayPlan,
  type PeriodRow,
} from "./timetableLogic";

export type Option = { id: number; name: string; level?: string };
export type Teacher = { id: number; first_name: string; last_name: string; user_type: string };
export const teacherName = (teacher: Teacher) => `${teacher.first_name} ${teacher.last_name}`.trim();

const REGENERATE = "Regenerate the timetable for lessons to follow.";

// --- Periods -------------------------------------------------------------------------

const periodColumns: RegisterColumn<PeriodRow>[] = [
  { header: "Order", cell: (row) => row.order, exportValue: (row) => row.order, align: "right" },
  { header: "Period", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
  { header: "Starts", cell: (row) => clock(row.start_time), exportValue: (row) => clock(row.start_time), className: "tabular-nums text-gray-700" },
  { header: "Ends", cell: (row) => clock(row.end_time), exportValue: (row) => clock(row.end_time), className: "tabular-nums text-gray-700" },
  { header: "Minutes", cell: periodMinutes, exportValue: periodMinutes, align: "right" },
  {
    header: "Kind",
    cell: (row) => <StatusDot label={kindLabel(row.kind)} dot={row.kind === "teaching" ? "bg-emerald-500" : "bg-gray-400"} />,
    exportValue: (row) => kindLabel(row.kind),
  },
  {
    header: "Not at school",
    cell: (row) => (row.excluded_levels?.length ? row.excluded_levels.join(", ") : <span className="text-gray-300">—</span>),
    exportValue: (row) => (row.excluded_levels ?? []).join(", "),
    className: "text-gray-700",
  },
  { header: "Lessons a week", cell: (row) => (row.kind !== "teaching" ? <span className="text-gray-300">—</span> : row.lessonCount), exportValue: (row) => row.lessonCount, align: "right" },
];

export function PeriodsPanel({ yearId, yearName, rows, levels, isLoading, canWrite, onChanged }: {
  yearId: number | null;
  yearName: string;
  rows?: PeriodRow[];
  /** The class levels in this year, in school order. */
  levels: string[];
  isLoading: boolean;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ record?: TimetablePeriod } | null>(null);
  const [planningDay, setPlanningDay] = useState(false);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const setDay = useMutation({
    mutationFn: (plan: DayPlan[]) => timetableService.setSchoolDay(yearId!, plan),
    onSuccess: (result) => {
      setPlanningDay(false);
      const classes = result.stranded_classes.join(", ");
      setNotice(
        result.stranded_lessons
          ? { tone: "error", text: `School day set. ${result.stranded_lessons} ${result.stranded_lessons === 1 ? "lesson now falls" : "lessons now fall"} at a time ${classes} ${result.stranded_classes.length === 1 ? "is" : "are"} not at school. Regenerate the timetable to move them.` }
          : { tone: "success", text: "School day set. Lessons already timetabled kept their periods." },
      );
      onChanged();
    },
  });
  const usage = useQuery({ queryKey: ["timetable-period", viewing], queryFn: () => timetableService.getPeriod(viewing!), enabled: viewing !== null && canWrite });
  const save = useMutation({
    mutationFn: ({ id, ...data }: Partial<TimetablePeriod> & { id?: number }) =>
      id ? timetableService.updatePeriod(id, data) : timetableService.createPeriod({ ...data, academic_year: yearId! }),
    onSuccess: (_result, variables) => {
      setEditing(null);
      setNotice({ tone: "success", text: variables.id ? `Period saved. ${REGENERATE}` : "Period added." });
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: timetableService.deletePeriod,
    onSuccess: () => {
      setDialog(null);
      setViewing(null);
      setNotice({ tone: "success", text: "Period deleted." });
      onChanged();
    },
    onError: (error) => {
      setDialog(null);
      setNotice({ tone: "error", text: deleteErrorText(error) });
    },
  });

  const gaps = periodGaps(rows ?? []);
  const period = viewing !== null && !editing && !dialog ? rows?.find((row) => row.id === viewing) : undefined;
  const nextOrder = Math.max(0, ...(rows ?? []).map((row) => row.order)) + 1;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {gaps.length > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Unrecorded time in the day: {gaps.map((gap) => `${gap.minutes} minutes between ${gap.after} and ${gap.before}`).join("; ")}. Add breaks so the week shows them.
        </p>
      )}
      <ClientRegister
        title={`Periods · ${yearName || "no year"}`}
        description="The school day, in order. Lessons are placed in teaching periods; breaks are shown on every week."
        actions={canWrite && (
          <>
            <button type="button" disabled={!yearId} onClick={() => { setDay.reset(); setPlanningDay(true); }} className="btn btn-outline h-8 min-h-8 gap-1.5 px-3 text-xs disabled:opacity-50">
              <CalendarRange className="h-3.5 w-3.5" /> Set the school day
            </button>
            <button type="button" disabled={!yearId} onClick={() => { save.reset(); setEditing({}); }} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New period
            </button>
          </>
        )}
        rows={rows}
        config={periodConfig}
        columns={periodColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["period", "periods"]}
        exportName={{ file: "timetable-periods", title: `Timetable Periods ${yearName}` }}
        isLoading={isLoading}
        emptyText={canWrite ? "No periods yet. Add the first period of the school day." : "No periods have been set for this year."}
      />
      {period && (
        <DetailsModal
          title={period.name}
          headline={{ label: kindLabel(period.kind), value: periodTime(period), note: `${periodMinutes(period)} minutes · number ${period.order} in the day` }}
          facts={[
            ["Order in the day", String(period.order)],
            ["Starts", clock(period.start_time)],
            ["Ends", clock(period.end_time)],
            ["Kind", kindLabel(period.kind)],
            ["Not at school", period.excluded_levels?.length ? period.excluded_levels.join(", ") : "Every class is in"],
            ["Lessons a week", String(period.lessonCount)],
          ]}
          notice={<UsageNotice inUse={usage.data?.in_use} advice="Change its times instead, or regenerate the timetable without it." />}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canWrite && usage.data && !describeUsage(usage.data.in_use) && (
                <button
                  type="button"
                  onClick={() => setDialog({
                    title: "Delete period?",
                    body: <p><strong>{period.name}</strong> will be removed from the school day. No lessons use it, and this cannot be undone.</p>,
                    confirmLabel: "Delete period",
                    tone: "danger",
                    onConfirm: () => remove.mutate(period.id),
                  })}
                  className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canWrite && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => { save.reset(); setEditing({ record: period }); }}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        />
      )}
      {planningDay && (
        <SchoolDayForm
          levels={levels}
          existing={rows ?? []}
          busy={setDay.isPending}
          error={setDay.isError ? apiErrorMessage(setDay.error, "The school day could not be set.") : undefined}
          onSave={(plan) => setDay.mutate(plan)}
          onClose={() => setPlanningDay(false)}
        />
      )}
      {editing && (
        <PeriodForm
          record={editing.record}
          levels={levels}
          nextOrder={nextOrder}
          busy={save.isPending}
          error={save.isError ? apiErrorMessage(save.error, "The period could not be saved.") : undefined}
          onSave={(data) => save.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={remove.isPending} />
    </div>
  );
}

function LevelChecks({ levels, chosen, onChange }: { levels: string[]; chosen: string[]; onChange: (next: string[]) => void }) {
  const nursery = levels.filter(isNurseryLevel);
  const has = (level: string) => chosen.includes(level);
  return (
    <div className="space-y-2">
      {nursery.length > 0 && (
        <button
          type="button"
          onClick={() => onChange(nursery.every(has) ? chosen.filter((level) => !nursery.includes(level)) : [...new Set([...chosen, ...nursery])])}
          className="text-xs font-medium text-primary-700 hover:underline"
        >
          {nursery.every(has) ? "Clear the nursery" : `Choose the nursery (${nursery.join(", ")})`}
        </button>
      )}
      <div className="flex flex-wrap gap-2">
        {levels.map((level) => (
          <label key={level} className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
            <input type="checkbox" checked={has(level)} onChange={() => onChange(has(level) ? chosen.filter((item) => item !== level) : [...chosen, level])} className="h-3.5 w-3.5 accent-primary-600" />
            {level}
          </label>
        ))}
      </div>
    </div>
  );
}

function PeriodForm({ record, levels, nextOrder, busy, error, onSave, onClose }: {
  record?: TimetablePeriod;
  levels: string[];
  nextOrder: number;
  busy: boolean;
  error?: string;
  onSave: (data: Partial<TimetablePeriod>) => void;
  onClose: () => void;
}) {
  const [start, setStart] = useState(clock(record?.start_time));
  const [end, setEnd] = useState(clock(record?.end_time));
  const [kind, setKind] = useState<TimetablePeriod["kind"]>(record?.kind ?? "teaching");
  const [excluded, setExcluded] = useState<string[]>(record?.excluded_levels ?? []);
  const wrongWay = Boolean(start && end && end <= start);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (wrongWay) return;
    const data = new FormData(event.currentTarget);
    onSave({
      name: String(data.get("name")).trim(),
      order: Number(data.get("order")),
      start_time: start,
      end_time: end,
      kind,
      excluded_levels: excluded,
    });
  };
  return (
    <FormModal
      id="period-form"
      title={record ? `Edit ${record.name}` : "New period"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Add period"}
      note="Periods in one year may not overlap."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Period" divided={false} columns={2}>
        <Field label="Name">
          <input required autoFocus name="name" defaultValue={record?.name} className="input mt-1" placeholder="e.g. Period 1, Break, Lunch" />
        </Field>
        <Field label="Order in the day" hint="1 for the first period of the day.">
          <input required type="number" min={1} name="order" defaultValue={record?.order ?? nextOrder} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Starts">
          <input required type="time" value={start} onChange={(event) => setStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Ends" hint={wrongWay ? <span className="text-red-700">It must end after it starts.</span> : undefined}>
          <input required type="time" value={end} onChange={(event) => setEnd(event.target.value)} aria-invalid={wrongWay} className="input mt-1" />
        </Field>
      </FormSection>
      <FormSection title="What happens in it" columns={1}>
        <div role="radiogroup" aria-label="Kind" className="grid gap-2 sm:grid-cols-2">
          {PERIOD_KINDS.map(([value, label, help]) => (
            <label key={value} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
              <input type="radio" name="kind" checked={kind === value} onChange={() => setKind(value)} className="mt-0.5 accent-primary-600" />
              <span>
                <span className="block font-medium text-gray-900">{label}</span>
                <span className="block text-xs text-gray-500">{help}</span>
              </span>
            </label>
          ))}
        </div>
      </FormSection>
      {levels.length > 0 && (
        <FormSection title="Classes not at school" description="Their lessons are never placed here, and their timetable shows it as home time." columns={1}>
          <LevelChecks levels={levels} chosen={excluded} onChange={setExcluded} />
        </FormSection>
      )}
    </FormModal>
  );
}

/**
 * The whole day at once, from the Ugandan pattern. Lessons already
 * timetabled keep their periods: the server re-times them in order.
 */
function SchoolDayForm({ levels, existing, busy, error, onSave, onClose }: {
  levels: string[];
  existing: TimetablePeriod[];
  busy: boolean;
  error?: string;
  onSave: (plan: DayPlan[]) => void;
  onClose: () => void;
}) {
  const [afternoonEnds, setAfternoonEnds] = useState<"16:30" | "17:00">("16:30");
  const [sports, setSports] = useState(true);
  const [homeAtOne, setHomeAtOne] = useState<string[]>(() => levels.filter(isNurseryLevel));
  const plan = ugandaSchoolDay({ afternoonEnds, sports, nurseryLevels: homeAtOne });
  const lessons = plan.filter((row) => row.kind === "teaching").length;
  const existingLessons = existing.filter((row) => row.kind === "teaching").length;
  return (
    <FormModal
      id="school-day-form"
      title="Set the school day"
      busy={busy}
      error={error}
      saveLabel="Set this day"
      note={existing.length ? "Existing lesson periods are re-timed in order; their lessons stay with them." : `${lessons} lessons a day for each class.`}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        onSave(plan);
      }}
    >
      <FormSection title="The day" description="Classes run side by side: in each lesson period, every class has its own lesson in its own room." divided={false} columns={2}>
        <Field label="Afternoon lessons end at">
          <select value={afternoonEnds} onChange={(event) => setAfternoonEnds(event.target.value as "16:30" | "17:00")} className="input mt-1">
            <option value="16:30">4:30 pm</option>
            <option value="17:00">5:00 pm</option>
          </select>
        </Field>
        <label className="flex cursor-pointer items-start gap-2.5 self-end rounded-lg border border-gray-200 p-3 text-sm has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
          <input type="checkbox" checked={sports} onChange={(event) => setSports(event.target.checked)} className="mt-0.5 h-4 w-4 accent-primary-600" />
          <span>
            <span className="block font-medium text-gray-900">Sports after classes</span>
            <span className="block text-xs text-gray-500">Half an hour for the whole school.</span>
          </span>
        </label>
      </FormSection>
      {levels.length > 0 && (
        <FormSection title="Classes that go home at 1 pm" description="Usually the nursery: the morning's four lessons, then home at lunch." columns={1}>
          <LevelChecks levels={levels} chosen={homeAtOne} onChange={setHomeAtOne} />
        </FormSection>
      )}
      <FormSection title="Preview" description="You can still change any period afterwards." columns={1}>
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <th>Time</th>
                <th>Period</th>
                <th>Kind</th>
                <th>Not at school</th>
              </tr>
            </thead>
            <tbody>
              {plan.map((row) => (
                <tr key={row.name} className={row.kind === "teaching" ? undefined : "bg-gray-50"}>
                  <td className="whitespace-nowrap tabular-nums">{periodTime(row)}</td>
                  <td className="font-medium text-gray-900">{row.name}</td>
                  <td>{kindLabel(row.kind)}</td>
                  <td className="text-gray-600">{row.excluded_levels.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {existingLessons > lessons && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
            This year has {existingLessons} lesson periods and the new day has {lessons}. An extra period that still has lessons is kept back; regenerate the timetable first to free it.
          </p>
        )}
      </FormSection>
    </FormModal>
  );
}

// --- Teaching assignments ----------------------------------------------------------

const assignmentColumns: RegisterColumn<Assignment>[] = [
  { header: "Teacher", cell: (row) => <span className="font-medium text-gray-900">{row.teacher_name}</span>, exportValue: (row) => row.teacher_name },
  { header: "Subject", cell: (row) => row.subject_name, exportValue: (row) => row.subject_name, className: "text-gray-700" },
  { header: "Class", cell: (row) => row.class_name, exportValue: (row) => row.class_name, className: "text-gray-700" },
  { header: "Assigned by", cell: (row) => row.assigned_by_name || <span className="text-gray-300">—</span>, exportValue: (row) => row.assigned_by_name ?? "", className: "text-gray-600" },
];

type Draft = { key: number; subject: string; classAssigned: string };
let nextDraft = 0;
const newDraft = (): Draft => ({ key: ++nextDraft, subject: "", classAssigned: "" });

export function AssignmentsPanel({ yearId, yearName, rows, isLoading, teachers, subjects, classes, canWrite, onChanged }: {
  yearId: number | null;
  yearName: string;
  rows?: Assignment[];
  isLoading: boolean;
  teachers: Teacher[];
  subjects: Option[];
  classes: Option[];
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ record?: Assignment } | null>(null);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const create = useMutation({
    mutationFn: async (data: { teacher: number; assignments: { subject: number; class_assigned: number }[] }) =>
      (await api.post("/academics/teacher/teacher-assignments/bulk/", { ...data, academic_year: yearId })).data as { created_count: number },
    onSuccess: (result) => {
      setEditing(null);
      setNotice({ tone: "success", text: `${result.created_count} ${result.created_count === 1 ? "assignment" : "assignments"} saved. ${REGENERATE}` });
      onChanged();
    },
  });
  const update = useMutation({
    mutationFn: async ({ id, ...data }: { id: number; teacher: number; subject: number; class_assigned: number }) =>
      (await api.patch(`/academics/teacher/teacher-assignments/${id}/`, { ...data, academic_year: yearId, is_active: true })).data,
    onSuccess: () => {
      setEditing(null);
      setNotice({ tone: "success", text: `Assignment updated. ${REGENERATE}` });
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: async (id: number) => api.delete(`/academics/teacher/teacher-assignments/${id}/`),
    onSuccess: () => {
      setDialog(null);
      setViewing(null);
      setNotice({ tone: "success", text: `Assignment removed. ${REGENERATE}` });
      onChanged();
    },
    onError: (error) => {
      setDialog(null);
      setNotice({ tone: "error", text: deleteErrorText(error) });
    },
  });

  const assignment = viewing !== null && !editing && !dialog ? rows?.find((row) => row.id === viewing) : undefined;
  const missing = [!teachers.length && "teachers", !subjects.length && "subjects (add them in Academics)", !classes.length && "classes for this year (add them in Classes & streams)"].filter(Boolean);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {canWrite && missing.length > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">Before assigning teachers there must be {missing.join(", ")}.</p>
      )}
      <ClientRegister
        title={`Teaching assignments · ${yearName || "no year"}`}
        description="Who teaches which subject to which class. They decide teachers' access to marks and homework, and what the timetable schedules."
        actions={canWrite && (
          <button type="button" disabled={!yearId || missing.length > 0} onClick={() => { create.reset(); setEditing({}); }} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Assign a teacher
          </button>
        )}
        rows={rows}
        config={assignmentConfig}
        columns={assignmentColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["assignment", "assignments"]}
        exportName={{ file: "teaching-assignments", title: `Teaching Assignments ${yearName}` }}
        isLoading={isLoading}
        emptyText="No teachers have been assigned for this year."
      />
      {assignment && (
        <DetailsModal
          title={`${assignment.subject_name} · ${assignment.class_name}`}
          headline={{ label: "Taught by", value: assignment.teacher_name, note: yearName }}
          facts={[
            ["Teacher", assignment.teacher_name],
            ["Subject", assignment.subject_name],
            ["Class", assignment.class_name],
            ["Assigned by", assignment.assigned_by_name],
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => setDialog({
                    title: "Remove assignment?",
                    body: <p><strong>{assignment.teacher_name}</strong> will no longer teach {assignment.subject_name} to {assignment.class_name}, and loses access to its marks and homework. Lessons already generated stay until you regenerate.</p>,
                    confirmLabel: "Remove assignment",
                    tone: "danger",
                    onConfirm: () => remove.mutate(assignment.id),
                  })}
                  className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" /> Remove
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canWrite && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => { update.reset(); setEditing({ record: assignment }); }}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        />
      )}
      {editing?.record && (
        <AssignmentEditForm
          record={editing.record}
          teachers={teachers}
          subjects={subjects}
          classes={classes}
          busy={update.isPending}
          error={update.isError ? apiErrorMessage(update.error, "The assignment could not be saved.") : undefined}
          onSave={(data) => update.mutate({ id: editing.record!.id, ...data })}
          onClose={() => setEditing(null)}
        />
      )}
      {editing && !editing.record && (
        <AssignmentsForm
          teachers={teachers}
          subjects={subjects}
          classes={classes}
          existing={rows ?? []}
          busy={create.isPending}
          error={create.isError ? apiErrorMessage(create.error, "The assignments could not be saved.") : undefined}
          onSave={(data) => create.mutate(data)}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={remove.isPending} />
    </div>
  );
}

function AssignmentsForm({ teachers, subjects, classes, existing, busy, error, onSave, onClose }: {
  teachers: Teacher[];
  subjects: Option[];
  classes: Option[];
  existing: Assignment[];
  busy: boolean;
  error?: string;
  onSave: (data: { teacher: number; assignments: { subject: number; class_assigned: number }[] }) => void;
  onClose: () => void;
}) {
  const [teacher, setTeacher] = useState("");
  const [drafts, setDrafts] = useState<Draft[]>([newDraft()]);
  const change = (key: number, changes: Partial<Draft>) => setDrafts((rows) => rows.map((row) => (row.key === key ? { ...row, ...changes } : row)));
  const pairs = drafts.map((row) => `${row.subject}:${row.classAssigned}`);
  const duplicate = (row: Draft, index: number) => Boolean(row.subject && row.classAssigned && pairs.indexOf(`${row.subject}:${row.classAssigned}`) !== index);
  const already = (row: Draft) =>
    existing.some((item) => String(item.teacher) === teacher && String(item.subject) === row.subject && String(item.class_assigned) === row.classAssigned);
  const problem = drafts.some(duplicate) ? "The same subject and class is listed twice." : drafts.some(already) ? "This teacher already teaches one of these subject-and-class pairs." : "";
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (problem) return;
    onSave({ teacher: Number(teacher), assignments: drafts.map((row) => ({ subject: Number(row.subject), class_assigned: Number(row.classAssigned) })) });
  };
  return (
    <FormModal
      id="assignments-form"
      title="Assign a teacher"
      busy={busy}
      error={error}
      saveLabel={`Save ${drafts.length} ${drafts.length === 1 ? "assignment" : "assignments"}`}
      note={REGENERATE}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Teacher" divided={false} columns={1}>
        <Field label="Teacher">
          <select required value={teacher} onChange={(event) => setTeacher(event.target.value)} className="input mt-1">
            <option value="">Choose a teacher</option>
            {teachers.map((item) => <option key={item.id} value={item.id}>{teacherName(item)} · {item.user_type.replaceAll("_", " ")}</option>)}
          </select>
        </Field>
      </FormSection>
      <FormSection title="What they teach" description="One line for each subject and class." columns={1}>
        <div className="space-y-2">
          {drafts.map((row, index) => (
            <div key={row.key} className={`grid items-end gap-2 rounded-lg border p-3 sm:grid-cols-[1fr_1fr_auto] ${duplicate(row, index) || already(row) ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-gray-50"}`}>
              <Field label={`Subject ${index + 1}`}>
                <select required value={row.subject} onChange={(event) => change(row.key, { subject: event.target.value })} className="input mt-1">
                  <option value="">Choose a subject</option>
                  {subjects.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </Field>
              <Field label={`Class ${index + 1}`}>
                <select required value={row.classAssigned} onChange={(event) => change(row.key, { classAssigned: event.target.value })} className="input mt-1">
                  <option value="">Choose a class</option>
                  {classes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </Field>
              <button
                type="button"
                aria-label={`Remove line ${index + 1}`}
                disabled={drafts.length === 1}
                onClick={() => setDrafts((rows) => rows.filter((item) => item.key !== row.key))}
                className="rounded p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setDrafts((rows) => [...rows, newDraft()])} className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline">
            <Plus className="h-4 w-4" /> Add another subject and class
          </button>
        </div>
      </FormSection>
      {problem && <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{problem}</p>}
    </FormModal>
  );
}

function AssignmentEditForm({ record, teachers, subjects, classes, busy, error, onSave, onClose }: {
  record: Assignment;
  teachers: Teacher[];
  subjects: Option[];
  classes: Option[];
  busy: boolean;
  error?: string;
  onSave: (data: { teacher: number; subject: number; class_assigned: number }) => void;
  onClose: () => void;
}) {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSave({ teacher: Number(data.get("teacher")), subject: Number(data.get("subject")), class_assigned: Number(data.get("class_assigned")) });
  };
  return (
    <FormModal
      id="assignment-edit-form"
      title="Edit assignment"
      busy={busy}
      error={error}
      saveLabel="Save changes"
      note={REGENERATE}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Assignment" divided={false} columns={1}>
        <Field label="Teacher">
          <select required name="teacher" defaultValue={record.teacher} className="input mt-1">
            {teachers.map((item) => <option key={item.id} value={item.id}>{teacherName(item)} · {item.user_type.replaceAll("_", " ")}</option>)}
          </select>
        </Field>
        <Field label="Subject">
          <select required name="subject" defaultValue={record.subject} className="input mt-1">
            {subjects.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </Field>
        <Field label="Class">
          <select required name="class_assigned" defaultValue={record.class_assigned} className="input mt-1">
            {classes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </Field>
      </FormSection>
    </FormModal>
  );
}
